import { Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule
} from '@angular/forms';
import { UserService } from "../../../@core/services/user.service";
import { ToastService } from "../../../@core/services/toast.service";
import { StorageService } from "../../../@core/services/storage.service";
import { AuthService } from "../../../@core/services/auth.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { InvitationTokenVerificationResponse, LoginResponse, UserResponse } from "../../../@shared/models/response.module";
import { USM_USR_19, USM_USR_34 } from "../../../@shared/models/input.module";
import { PostResponse } from "../../../@shared/models/global/response-wrapper.service";
import {CommonModule} from "@angular/common";

@Component({
  selector: 'app-register-user',
  standalone: true,
  templateUrl: './register-user.component.html',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterLink
  ],
  styleUrl: './register-user.component.css'
})
export class RegisterUserComponent {
  form: FormGroup;
  registered = false;
  isSubmitting = false;

  constructor(
    private toastService: ToastService,
    private userService: UserService,
    private storageService: StorageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.form = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      phone_prefix: new FormControl('+47', [Validators.required]),
      phone_number: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{8,15}$/)]),
      first_name: new FormControl('', [Validators.required]),
      last_name: new FormControl('', [Validators.required]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator // Custom Validator
      ]),
      confirmPassword: new FormControl('', [Validators.required])
    }, { validators: this.passwordMatchValidator });

    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/dashboard']);
    }

    this.route.queryParams.subscribe(params => {
      this.form.patchValue({
        email: params['email'],
      });
    });
  }

  onSubmit(): void {
    if (this.form.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const phonePrefix = this.form.get('phone_prefix')!.value;
      const phoneNumber = this.form.get('phone_number')!.value;
      const payload: USM_USR_34 = {
        email: this.form.get('email')!.value,
        phone_number: `${phonePrefix}${phoneNumber}`,
        password: this.form.get('password')!.value,
        first_name: this.form.get('first_name')!.value,
        last_name: this.form.get('last_name')!.value,
      };
      this.authService.registerPublicUser(payload).subscribe(
        (response: PostResponse<UserResponse>) => {
          this.registered = true;
          this.isSubmitting = false;
          this.toastService.showSuccess('Registration successful! Please check your email to verify your account.', 'Success');
        },
        (error) => {
          this.isSubmitting = false;
          this.toastService.error('Registration failed. Please try again.');
        }
      );
    }
  }

  // Custom Validator: Password Strength
  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password: string = control.value || '';

    if (!password) return null; // Don't validate if empty

    const hasNumber = /\d/.test(password); // Check for number
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password); // Check for special character
    const validLength = password.length >= 8;

    if (!hasNumber || !hasSpecialChar || !validLength) {
      return { passwordStrength: true };
    }
    return null;
  }

  // Custom Validator: Confirm Password Match
  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  passwordVisible: boolean = false;
  confirmPasswordVisible: boolean = false;

  togglePasswordVisibility(inputId: string): void {
    if (inputId === 'password') {
      this.passwordVisible = !this.passwordVisible;
    } else if (inputId === 'confirmPassword') {
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
    }

    const inputElement = document.getElementById(inputId) as HTMLInputElement;
    inputElement.type = inputElement.type === 'password' ? 'text' : 'password';
  }
}
